import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { agentService } from '../../services/agentService';
import { authService } from '../../services/authService';
import type { InvitationDetails, RegistrationError } from '../../types/agent';

export const AgentRegister: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  // Invitation details
  const [invitation, setInvitation] = useState<InvitationDetails | null>(null);
  const [loadingInvitation, setLoadingInvitation] = useState(true);
  const [invitationError, setInvitationError] = useState<string | null>(null);

  // Form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);

  // Fetch invitation details on mount
  useEffect(() => {
    const fetchInvitation = async () => {
      if (!token) {
        setInvitationError(t('agentRegister.noToken'));
        setLoadingInvitation(false);
        return;
      }

      try {
        const details = await agentService.getInvitationDetails(token);

        // Check if invitation is already used
        if (details.isUsed) {
          setInvitationError(t('agentRegister.alreadyUsed'));
          setLoadingInvitation(false);
          return;
        }

        // Check if invitation is expired
        const expiresAt = new Date(details.expiresAt);
        if (expiresAt < new Date()) {
          setInvitationError(t('agentRegister.expired'));
          setLoadingInvitation(false);
          return;
        }

        setInvitation(details);
      } catch (err) {
        setInvitationError(
          err instanceof Error ? err.message : t('agentRegister.errors.registrationFailed')
        );
      } finally {
        setLoadingInvitation(false);
      }
    };

    fetchInvitation();
  }, [token, t]);

  const validateForm = (): boolean => {
    const errors: string[] = [];

    if (!firstName.trim()) errors.push(t('agentRegister.validation.firstNameRequired'));
    if (!lastName.trim()) errors.push(t('agentRegister.validation.lastNameRequired'));
    if (!password) errors.push(t('agentRegister.validation.passwordRequired'));
    if (password.length < 6) errors.push(t('agentRegister.validation.passwordTooShort'));
    if (password !== confirmPassword) errors.push(t('agentRegister.validation.passwordsDoNotMatch'));

    setValidationErrors(errors);
    return errors.length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setValidationErrors([]);

    if (!validateForm()) return;
    if (!token) {
      setError(t('agentRegister.noToken'));
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await agentService.registerAgent({
        invitationToken: token,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        password,
        phone: phone.trim() || undefined,
        dateOfBirth: dateOfBirth || undefined,
      });

      // Store authentication token
      authService.storage.setToken(response.token);
      authService.storage.setUser(JSON.stringify(response.user));

      // Show success confirmation before redirecting
      setRegistrationSuccess(true);
      setTimeout(() => {
        navigate('/', { replace: true });
      }, 2500);
    } catch (err: any) {
      // Handle API errors with new error format
      if (err.data && err.data.error) {
        const errorCode = err.data.error;
        const errorMessage = err.data.message;

        // Map error codes to user-friendly messages
        switch (errorCode) {
          case 'EMAIL_EXISTS':
            setError(t('agentRegister.errors.emailExists'));
            break;
          case 'WEAK_PASSWORD':
            setError(t('agentRegister.errors.weakPassword'));
            break;
          case 'INVALID_INVITATION':
            setError(t('agentRegister.errors.invalidInvitation'));
            setInvitationError(t('agentRegister.errors.invalidInvitation'));
            break;
          default:
            setError(errorMessage || t('agentRegister.errors.registrationFailed'));
        }
      } else if (err.data) {
        // Fallback for old error format
        const apiError = err.data as RegistrationError;
        setError(apiError.message);
        if (apiError.details) {
          setValidationErrors(apiError.details);
        }
      } else {
        setError(err instanceof Error ? err.message : t('agentRegister.errors.registrationFailed'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading state
  if (loadingInvitation) {
    return (
      <div className="min-h-screen bg-[#23262b] flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#4d63ac] mb-4"></div>
          <p className="text-[#9aa3b2]">{t('agentRegister.loading')}</p>
        </div>
      </div>
    );
  }

  // Success state
  if (registrationSuccess) {
    return (
      <div className="min-h-screen bg-[#23262b] flex items-center justify-center p-6">
        <div className="bg-[#2c3036] rounded-lg border border-white/[0.08] p-8 max-w-md w-full text-center">
          <div className="text-6xl mb-4">✅</div>
          <h2 className="text-2xl font-bold text-white mb-2">
            {t('agentRegister.success.title', 'Konto zostało utworzone')}
          </h2>
          <p className="text-[#9aa3b2]">
            {t('agentRegister.success.message', 'Za chwilę zostaniesz przekierowany do aplikacji.')}
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (invitationError || !invitation) {
    return (
      <div className="min-h-screen bg-[#23262b] flex items-center justify-center p-6">
        <div className="bg-[#2c3036] rounded-lg border border-white/[0.08] p-8 max-w-md w-full text-center">
          <div className="text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-white mb-2">{t('agentRegister.invalidInvitation')}</h2>
          <p className="text-[#9aa3b2] mb-6">{invitationError}</p>
          <button
            onClick={() => navigate('/login')}
            className="px-6 py-2 bg-[#4d63ac] text-white rounded-lg hover:bg-[#5b71be] transition-colors"
          >
            {t('agentRegister.goToLogin')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#23262b] flex items-center justify-center p-6">
      <div className="bg-[#2c3036] rounded-lg border border-white/[0.08] p-8 max-w-2xl w-full">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">{t('agentRegister.title')}</h1>
          <p className="text-[#9aa3b2]">
            {t('agentRegister.invitedToJoin')} <span className="text-[#7d8dcd] font-semibold">{invitation.businessProfileName}</span>
          </p>
          <p className="text-[#9aa3b2] text-sm mt-1">
            {t('agentRegister.invitedBy')} {invitation.invitedByUserName}
          </p>
        </div>

        {/* Email (read-only) */}
        <div className="mb-6 p-4 bg-[#2a2e34]/60 rounded-lg border border-white/[0.08]">
          <label className="block text-sm font-medium text-[#9aa3b2] mb-1">{t('agentRegister.email')}</label>
          <div className="text-white font-medium">{invitation.email}</div>
        </div>

        {/* Error messages */}
        {(error || validationErrors.length > 0) && (
          <div className="mb-6 p-4 bg-[#c06f57]/10 border border-[#c06f57]/30 rounded-lg">
            {error && <p className="text-[#e1a08a] font-medium mb-2">{error}</p>}
            {validationErrors.length > 0 && (
              <ul className="text-[#e1a08a] text-sm space-y-1">
                {validationErrors.map((err, index) => (
                  <li key={index}>• {err}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Name fields */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#c5cde0] mb-2">
                {t('agentRegister.firstName')} {t('agentRegister.required')}
              </label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="w-full px-3 py-2 bg-[#2a2e34] border border-white/[0.10] rounded-lg text-white placeholder-[#6b7280] focus:outline-none focus:ring-2 focus:ring-[#4d63ac]"
                placeholder={t('agentRegister.placeholders.firstName')}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#c5cde0] mb-2">
                {t('agentRegister.lastName')} {t('agentRegister.required')}
              </label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="w-full px-3 py-2 bg-[#2a2e34] border border-white/[0.10] rounded-lg text-white placeholder-[#6b7280] focus:outline-none focus:ring-2 focus:ring-[#4d63ac]"
                placeholder={t('agentRegister.placeholders.lastName')}
                required
              />
            </div>
          </div>

          {/* Password fields */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#c5cde0] mb-2">
                {t('agentRegister.password')} {t('agentRegister.required')}
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 bg-[#2a2e34] border border-white/[0.10] rounded-lg text-white placeholder-[#6b7280] focus:outline-none focus:ring-2 focus:ring-[#4d63ac]"
                placeholder={t('agentRegister.placeholders.password')}
                required
                minLength={6}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#c5cde0] mb-2">
                {t('agentRegister.confirmPassword')} {t('agentRegister.required')}
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-3 py-2 bg-[#2a2e34] border border-white/[0.10] rounded-lg text-white placeholder-[#6b7280] focus:outline-none focus:ring-2 focus:ring-[#4d63ac]"
                placeholder={t('agentRegister.placeholders.password')}
                required
              />
            </div>
          </div>

          {/* Phone */}
          <div>
            <label className="block text-sm font-medium text-[#c5cde0] mb-2">
              {t('agentRegister.phoneNumber')} ({t('agentRegister.optional')})
            </label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full px-3 py-2 bg-[#2a2e34] border border-white/[0.10] rounded-lg text-white placeholder-[#6b7280] focus:outline-none focus:ring-2 focus:ring-[#4d63ac]"
              placeholder={t('agentRegister.placeholders.phone')}
            />
          </div>

          {/* Date of Birth */}
          <div>
            <label className="block text-sm font-medium text-[#c5cde0] mb-2">
              {t('agentRegister.dateOfBirth')} ({t('agentRegister.optional')})
            </label>
            <input
              type="date"
              value={dateOfBirth}
              onChange={(e) => setDateOfBirth(e.target.value)}
              className="w-full px-3 py-2 bg-[#2a2e34] border border-white/[0.10] rounded-lg text-white placeholder-[#6b7280] focus:outline-none focus:ring-2 focus:ring-[#4d63ac]"
            />
          </div>

          {/* Submit button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full px-6 py-3 bg-[#4d63ac] text-white rounded-lg hover:bg-[#5b71be] transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? t('agentRegister.registering') : t('agentRegister.completeRegistration')}
          </button>
        </form>

        <p className="text-center text-[#9aa3b2] text-sm mt-6">
          {t('agentRegister.alreadyHaveAccount')}{' '}
          <button
            onClick={() => navigate('/login')}
            className="text-[#7d8dcd] hover:text-[#9aa8d9]"
          >
            {t('agentRegister.signIn')}
          </button>
        </p>
      </div>
    </div>
  );
};
